// OrderForm.js
import React, { useState, useRef, useCallback } from "react";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { v4 as uuidv4 } from "uuid";
import ProductEditor from "./ProductEditor";


function getQuillModules(productId, handleImageUpload) {
  return {
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["blockquote", "code-block"],
        [{ align: [] }],
        ["link", "image"],
        ["clean"]
      ],
      handlers: {
        image: () => handleImageUpload(productId)
      }
    }
  };
}

const OrderForm = () => {
  const [formData, setFormData] = useState({
    customerName: "",
    phoneNumber: "",
    address: "",
    orderDate: new Date().toISOString().split("T")[0],
    Dtime: new Date(new Date().setDate(new Date().getDate() + 12))
      .toISOString()
      .split("T")[0],
    products: [
      {
        id: uuidv4(),
        modelName: "",
        productType: "necklace",
        modelPhoto: null,
        customName: "",
        dealtPrice: "",
        goldRate: "",
        dealtWeight: "",
        notes: "",
        guarantee: "free"
      }
    ]
  });

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Instead of an array, store refs in an object keyed by product.id
  const quillRefs = useRef({});

  // ----------------------------------
  // UTILITY HANDLERS
  // ----------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProductChange = (index, field, value) => {
    const updatedProducts = [...formData.products];
    updatedProducts[index][field] = value;
    setFormData((prev) => ({ ...prev, products: updatedProducts }));
  };

  const handleModelPhotoChange = (index, file) => {
    const updatedProducts = [...formData.products];
    updatedProducts[index].modelPhoto = file;
    setFormData((prev) => ({ ...prev, products: updatedProducts }));
  };

  // We'll store the notes in the "products" array based on matching product ID
  const handleNotesChange = (id, value) => {
    const updatedProducts = formData.products.map((p) =>
      p.id === id ? { ...p, notes: value } : p
    );
    setFormData((prev) => ({ ...prev, products: updatedProducts }));
  };

  // UseCallback so it isn't recreated on every render
  const handleImageUpload = useCallback(
    (productId) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.click();

      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;

        const formImg = new FormData();
        formImg.append("image", file);

        try {
          const response = await axios.post(
            "/api/orders/upload",
            formImg
          );
          const imageUrl = response.data.imageUrl;

          const editor = quillRefs.current[productId]?.getEditor();
          const range = editor.getSelection();
          editor.insertEmbed(range.index, "image", imageUrl);
        } catch (error) {
          console.error("Image upload failed:", error);
        }
      };
    },
    [quillRefs]
  );

  // ----------------------------------
  // FORM SUBMISSION
  // ----------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
  
    // Create a single FormData object
    const form = new FormData();
    form.append("customerName", formData.customerName);
    form.append("phoneNumber", formData.phoneNumber);
    form.append("address", formData.address);
    form.append("orderDate", formData.orderDate);
    form.append("Dtime", formData.Dtime);
  
    // Append each product's modelPhoto file (if any)
    formData.products.forEach((product) => {
      if (product.modelPhoto) {
        form.append("modelPhoto", product.modelPhoto);
      }
    });
  
    // Remove the file (modelPhoto) from each product because File objects cannot be JSON-stringified
    const productsData = formData.products.map(({ modelPhoto, ...rest }) => rest);
  
    // Append the entire products array as a single JSON string
    form.append("products", JSON.stringify(productsData));
  
    try {
      const response = await axios.post(
        "/api/orders/create",
        form,
        {
          headers: { "Content-Type": "multipart/form-data" }
        }
      );
  
      if (response.data) {
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
  
        // Reset form data
        setFormData({
          customerName: "",
          phoneNumber: "",
          address: "",
          orderDate: new Date().toISOString().split("T")[0],
          Dtime: new Date(new Date().setDate(new Date().getDate() + 12))
            .toISOString()
            .split("T")[0],
          products: [
            {
              id: uuidv4(),
              modelName: "",
              productType: "necklace",
              modelPhoto: null,
              customName: "",
              dealtPrice: "",
              goldRate: "",
              dealtWeight: "",
              notes: "",
              guarantee: "free"
            }
          ]
        });
      }
    } catch (error) {
      console.error("Error submitting order:", error);
      alert("Failed to submit order.");
    } finally {
      setIsSubmitting(false);
    }
  };
  

  // ----------------------------------
  // RENDER
  // ----------------------------------
  return (
    <>
      {showSuccessMessage && (
        <div className="fixed left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          ✅ Order created successfully!
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="max-w-lg mx-auto p-4 border rounded-lg shadow-lg bg-white"
      >
        <h2 className="text-xl font-bold mb-4">Add New Order</h2>

        <input
          type="text"
          name="customerName"
          placeholder="Customer Name"
          value={formData.customerName}
          onChange={handleChange}
          className="w-full p-2 border mb-2"
          required
        />

        <input
          type="text"
          name="phoneNumber"
          placeholder="Phone Number"
          value={formData.phoneNumber}
          onChange={handleChange}
          className="w-full p-2 border mb-2"
          required
        />

        <input
          type="text"
          name="address"
          placeholder="Address"
          value={formData.address}
          onChange={handleChange}
          className="w-full p-2 border mb-2"
          required
        />

        <input
          type="date"
          name="orderDate"
          value={formData.orderDate}
          onChange={handleChange}
          className="w-full p-2 border mb-2"
          required
        />

        <input
          type="date"
          name="Dtime"
          value={formData.Dtime}
          onChange={handleChange}
          className="w-full p-2 border mb-2"
          required
        />

        {formData.products.map((product, index) => {
          // We define modules for each product right here, no map, no loop
          const modules = {
            toolbar: {
              container: [
                [{ header: [1, 2, 3, false] }],
                ["bold", "italic", "underline", "strike"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["blockquote", "code-block"],
                [{ align: [] }],
                ["link", "image"],
                ["clean"]
              ],
              handlers: {
                image: () => handleImageUpload(product.id)
              }
            }
          };

          return (
            <div key={product.id} className="border p-4 mb-4 rounded-md bg-gray-50">
              <h3 className="font-semibold text-md mb-2">
                Product {index + 1}
              </h3>

              <input
                type="text"
                placeholder="Model Name"
                value={product.modelName}
                onChange={(e) =>
                  handleProductChange(index, "modelName", e.target.value)
                }
                className="w-full p-2 border mb-2"
                required
              />

              <select
                value={product.productType}
                onChange={(e) =>
                  handleProductChange(index, "productType", e.target.value)
                }
                className="w-full p-2 border mb-2 rounded"
              >
                <option value="necklace">Necklace</option>
                <option value="bracelet">Bracelet</option>
                <option value="ring">Ring</option>
                <option value="earring">Earring</option>
                <option value="brooch">Brooch</option>
              </select>

              <input
                type="file"
                onChange={(e) => handleModelPhotoChange(index, e.target.files[0])}
                className="w-full p-2 border mb-2"
              />

              <input
                type="text"
                placeholder="Custom Name"
                value={product.customName}
                onChange={(e) =>
                  handleProductChange(index, "customName", e.target.value)
                }
                className="w-full p-2 border mb-2"
              />

              <input
                type="number"
                placeholder="Dealt Price"
                value={product.dealtPrice}
                onChange={(e) =>
                  handleProductChange(index, "dealtPrice", e.target.value)
                }
                className="w-full p-2 border mb-2"
                required
              />

              <input
                type="number"
                placeholder="Gold Rate"
                value={product.goldRate}
                onChange={(e) =>
                  handleProductChange(index, "goldRate", e.target.value)
                }
                className="w-full p-2 border mb-2"
                required
              />

              <input
                type="number"
                placeholder="Dealt Weight"
                value={product.dealtWeight}
                onChange={(e) =>
                  handleProductChange(index, "dealtWeight", e.target.value)
                }
                className="w-full p-2 border mb-2"
                required
              />

              {/* Quill Editor */}
              <ProductEditor
                product={product}
                handleNotesChange={handleNotesChange}
                handleImageUpload={handleImageUpload}
                quillRef={(el) => (quillRefs.current[product.id] = el)}
              />


              <div className="flex gap-4 mb-2">
                <label>
                  <input
                    type="checkbox"
                    checked={product.guarantee === "paid"}
                    onChange={() =>
                      handleProductChange(
                        index,
                        "guarantee",
                        product.guarantee === "paid" ? "none" : "paid"
                      )
                    }
                  />
                  <span className="ml-1">Paid Guarantee (+$5)</span>
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={product.guarantee === "free"}
                    onChange={() =>
                      handleProductChange(
                        index,
                        "guarantee",
                        product.guarantee === "free" ? "none" : "free"
                      )
                    }
                  />
                  <span className="ml-1">Free Guarantee</span>
                </label>
              </div>
            </div>
          );
        })}

        <div className="text-center mb-4">
          <button
            type="button"
            onClick={() =>
              setFormData((prev) => ({
                ...prev,
                products: [
                  ...prev.products,
                  {
                    id: uuidv4(),
                    modelName: "",
                    productType: "necklace",
                    modelPhoto: null,
                    customName: "",
                    dealtPrice: "",
                    goldRate: "",
                    dealtWeight: "",
                    notes: "",
                    guarantee: "free"
                  }
                ]
              }))
            }
            className="text-blue-600 underline"
          >
            ➕ Add Another Product
          </button>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full p-2 text-white rounded bg-blue-600"
        >
          {isSubmitting ? "Submitting..." : "Submit Order"}
        </button>
      </form>
    </>
  );
};

export default OrderForm;
